<template>
  <or-modal
    ref="attach-assets-modal"
    class="attach-assets-modal"
    @close="$emit('hide')"
  >
    <template #header>
      {{ $t('hitl.common.addFile') }}
    </template>

    <template #default>
      <or-asset-manager
        v-if="isOpen"
        v-model="files"
        :auth-token="getToken"
        :files-api-url="mediaURL"
        :allow-search="false"
        :allowed-file-types="allowedFileTypes"
        :multi-select="multiSelect"
        :use-files="true"
      />
    </template>

    <template #footer>
      <div class="actions">
        <or-button
          :variant="ButtonVariant.Outlined"
          data-button="reset"
          @click="handleAttachReset"
        >
          {{ $t('hitl.common.reset') }}
        </or-button>

        <or-button
          :disabled="!canAttachFiles"
          data-button="attach"
          @click="handleAttachAccept"
        >
          {{ $t('hitl.common.attachFile') }}
        </or-button>
      </div>
    </template>
  </or-modal>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from '@vue/composition-api';
import {
  OrModalV3 as OrModal,
  OrButtonV3 as OrButton,
  ButtonVariant,
  ModalSize,
} from '@onereach/ui-components-25';

import { IFileMetaInfo, IModal } from '@/types';
import { useHelpers, useAuth } from '@/composables';

export default defineComponent({
  name: 'AttachAssetsModal',
  components: {
    OrModal,
    OrButton,
  },
  props: {
    isOpen: Boolean,
    originalFiles: {
      type: Array as PropType<IFileMetaInfo[]>,
      default: () => [],
    },
    multiSelect: {
      type: Boolean,
      default: true,
    },
    allowedFileTypes: {
      type: Array,
      default: () => {
        const allowed = [];

        for (const type of DEFAULT_ALLOWED_FILE_TYPES) {
          allowed.push(type, type.toUpperCase());
        }

        return allowed;
      },
    },
  },
  setup() {
    const { mediaURL } = useHelpers();
    const { getToken } = useAuth();
    return {
      getToken,
      mediaURL,
      ButtonVariant,
      ModalSize,
    };
  },
  data() {
    return {
      files: [] as IFileMetaInfo[],
    };
  },
  computed: {
    canAttachFiles(): boolean {
      return !_.isEmpty(this.files)
        && this.files.every((file: IFileMetaInfo): boolean => !!file.location);
    },
  },
  watch: {
    isOpen(isOpen: boolean): void {
      if (isOpen) {
        (this.$refs['attach-assets-modal'] as IModal).open();
        this.files = _.cloneDeep(this.originalFiles);
      } else {
        (this.$refs['attach-assets-modal'] as IModal).close();
        this.files = [];
      }
    },
  },
  methods: {
    handleAttachReset(): void {
      this.$emit('attach', []);
      (this.$refs['attach-assets-modal'] as IModal).close();
    },

    handleAttachAccept(): void {
      this.$emit('attach', this.files);
      (this.$refs['attach-assets-modal'] as IModal).close();
    },
  },
});

const DEFAULT_ALLOWED_FILE_TYPES = [
  'mp3',
  'mp4',
  'wav',
  'jpg',
  'jpeg',
  'gif',
  'png',
  'json',
  'csv',
  'txt',
  'log',
  'md',
  'avi',
  'pdf',
];
</script>

<style lang="scss" scoped>
  .actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--s-3);
  }
</style>
